// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CreateNewGroupModal from "@bg/App/Components/RfqRequestQuote/CreateNewGroupModal";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartModel, ProductDto } from "@insite/client-framework/Types/ApiModels";
import OrderUpload, { OrderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import OrderUploadErrorsModal, {
    OrderUploadErrorsModalStyles,
} from "@insite/content-library/Components/OrderUploadErrorsModal";
import ProductSelector, { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import ProductSelectorVariantModal from "@insite/content-library/Components/ProductSelectorVariantModal";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cartLength?: number;
    cart?: CartModel;
    widgetId: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
});

const mapDispatchToProps = {
    addCartLines: makeHandlerChainAwaitable(addCartLines as any),
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface RequestQuoteOptionsStyle {
    container?: InjectableCss;
    countsWrapper?: InjectableCss;
    count?: TypographyPresentationProps;
    addItems?: LinkPresentationProps;
    createGroupWrapper?: InjectableCss;
    createGroup?: LinkPresentationProps;
    addItemsGridContainer?: InjectableCss;
    productSelectorGridItem?: InjectableCss;
    productSelectorStyles?: ProductSelectorStyles;
    uploadItemsGridItem?: InjectableCss;
    uploadItemsLink?: LinkPresentationProps;
    uploadItemsModal?: ModalPresentationProps;
    orderUploadStyles?: OrderUploadStyles;
    orderUploadErrorsModalStyles?: OrderUploadErrorsModalStyles;
    closeAddItemsToList?: IconProps;
    actionsPopup?: InjectableCss;
}

export const styles: RequestQuoteOptionsStyle = {
    container: {
        css: css`
            padding: 12px 0;
        `,
    },
    countsWrapper: {
        css: css`
            display: flex;
            position: relative;
        `,
    },
    count: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
            margin-right: 30px;
        `,
    },
    addItems: linkIconStyleGenerator({
        icon: "AddToList",
        textSize: "15px",
        iconSize: 12,
        additionalTypographyCss: css`
            font-weight: ${fontConfig.weight.semiBold};
        `,
    }),
    createGroupWrapper: {
        css: css`
            flex-grow: 1;
            display: flex;
            justify-content: flex-end;
            @media (max-width: 481px) {
                justify-content: flex-start;
                width: 100%;
            }
        `,
    },
    createGroup: linkIconStyleGenerator({
        icon: "CreateGroup",
        textSize: "15px",
        iconSize: 14,
        additionalTypographyCss: css`
            opacity: 0.8;
            font-weight: ${fontConfig.weight.semiBold};
        `,
    }),
    productSelectorGridItem: {
        css: css`
            display: flex;
            @media (max-width: 680px) {
                flex-wrap: wrap;
            }
        `,
    },
    productSelectorStyles: {
        container: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                padding: 0;
                width: 100%;
                align-items: center;

                .select-product {
                    margin-top: 0 !important;
                }
            `,
        },
        searchWrapper: {
            css: css`
                max-width: 450px;
                width: 100%;
                margin-right: 30px;
                @media (max-width: 1025px) {
                    margin-bottom: 15px;
                }
                @media (max-width: 520px) {
                    margin-right: 0px;
                    margin-bottom: 15px;
                    max-width: unset;
                }
            `,
        },
        searchDynamicDropdown: {
            cssOverrides: {
                formField: css`
                    flex-direction: row;
                    align-items: center;
                    width: 100%;

                    label {
                        width: auto;
                        font-weight: ${fontConfig.weight.bold};
                        font-size: ${fontConfig.fontSize.small}px;
                        margin-right: 8px;
                        white-space: nowrap;
                    }
                    @media (max-width: 650px) {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                `,
                formInputWrapper: css`
                    margin-top: 0 !important;
                `,
                inputSelect: css`
                    font-size: ${fontConfig.fontSize.small}px;
                    height: 32px;
                    @media (max-width: 520px) {
                        height: 40px;
                    }
                `,
                selectedText: css`
                    padding: 5px 10px;
                `,
                list: css`
                    top: 32px;
                    width: 100%;
                    border-top: 0;
                `,
            },
            iconProps: {
                css: css`
                    &&&&&& {
                        right: 0;
                        top: 0;
                        @media (max-width: 520px) {
                            right: 2px;
                            top: 4px;
                        }
                    }
                `,
            },
        },
        qtyWrapper: {
            css: css`
                display: flex;
                align-items: center;
                width: 160px;
                padding: 0;
                margin-right: 30px;
                @media (max-width: 520px) {
                    width: 100%;
                    margin-right: 0px;
                }
                &&& > label {
                    width: auto;
                    font-weight: ${fontConfig.weight.bold};
                    font-size: ${fontConfig.lineHeight.small}px;
                    margin-right: 8px;
                    white-space: nowrap;
                }

                label + div {
                    margin-top: 0;
                    height: 32px;
                    @media (max-width: 520px) {
                        height: 40px;
                    }
                }

                &&& input {
                    height: 32px !important;
                    @media (max-width: 520px) {
                        height: 40px !important;
                    }
                }
            `,
        },
        selectButton: {
            css: css`
                min-height: 32px !important;
                margin-top: 0 !important;
            `,
        },
    } as ProductSelectorStyles,
    uploadItemsLink: {
        css: css`
            margin: 0;
            white-space: nowrap;
            margin-right: 15px;
            height: 32px;
        `,
    },
    closeAddItemsToList: {
        src: "X",
        css: css`
            margin: 0;
            margin-top: 5px;
            cursor: pointer;
            align-items: flex-start;
        `,
    },
    actionsPopup: {
        css: css`
            position: absolute;
            right: 0;
            top: -10px;
            @media (max-width: 481px) {
                nav {
                    height: 34px;
                    width: 34px;
                }
                nav > button:first-child {
                    min-height: 34px;
                    height: 34px;
                    width: 34px;
                }
            }
        `,
    },
};

const RfqRequestQuoteOptions = ({ cartLength, cart, widgetId, addCartLines, uncategorizedGroupName }: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [addItemsToListIsOpen, setAddItemsToListIsOpen] = useState(false);
    const [uploadItemsModalIsOpen, setUploadItemsModalIsOpen] = useState(false);
    const [groupingModalIsOpen, setGroupingModalIsOpen] = useState(false);
    const [productInfo, setProductInfo] = useState<ProductInfo>({} as ProductInfo);
    const [creatingNewGroup, setIsCreatingNewGroup] = useState(false);

    if (!cart) {
        return null;
    }

    const selectProductHandler = (productInfo: ProductInfo) => {
        setGroupingModalIsOpen(true);
        setProductInfo(productInfo);
    };

    const uploadItemsClickHandler = () => {
        setUploadItemsModalIsOpen(true);
    };

    const uploadItemsModalCloseHandler = () => {
        setUploadItemsModalIsOpen(false);
    };

    const uploadProductsHandler = async (products: ProductDto[]) => {
        const productInfos = products.map(o => ({
            productId: o.id,
            qtyOrdered: o.qtyOrdered,
            unitOfMeasure: o.selectedUnitOfMeasure ?? "",
        }));
        const result = (await addCartLines({
            productInfos,
            newGroup: true,
            groupName: uncategorizedGroupName,
            loadCurrentCart: true,
        })) as CartLineCollectionModel;

        if (result.cartLines?.length) {
            toasterContext.addToast({ body: translate("Items Added"), messageType: "success" });
        }

        setUploadItemsModalIsOpen(false);
    };

    const openCreateGroupModal = () => {
        setGroupingModalIsOpen(true);
        setIsCreatingNewGroup(true);
    };

    const closeModalHandler = () => {
        setGroupingModalIsOpen(false);
        setIsCreatingNewGroup(false);
    };

    return (
        <StyledWrapper {...styles.container}>
            {!addItemsToListIsOpen && (
                <StyledWrapper {...styles.countsWrapper}>
                    <Typography {...styles.count}>
                        {cartLength} {translate("Products")}
                    </Typography>
                    <Hidden
                        below="md"
                        css={css`
                            display: flex;
                            flex-grow: 1;
                        `}
                    >
                        <Link onClick={() => setAddItemsToListIsOpen(true)} {...styles.addItems}>
                            {translate("Add Items To Quote")}
                        </Link>
                        <StyledWrapper {...styles.createGroupWrapper}>
                            <Link onClick={openCreateGroupModal} {...styles.createGroup}>
                                {translate("Create a New Group")}
                            </Link>
                        </StyledWrapper>
                    </Hidden>
                    <Hidden above="sm" {...styles.actionsPopup}>
                        <OverflowMenu>
                            <Clickable onClick={() => setAddItemsToListIsOpen(true)}>
                                {translate("Add Items To Quote")}
                            </Clickable>
                            <Clickable onClick={openCreateGroupModal}> {translate("Create a New Group")}</Clickable>
                        </OverflowMenu>
                    </Hidden>
                </StyledWrapper>
            )}
            {addItemsToListIsOpen && (
                <StyledWrapper {...styles.addItemsGridContainer}>
                    <StyledWrapper {...styles.productSelectorGridItem}>
                        <ProductSelector
                            selectButtonTitle={translate("Add to Quote")}
                            skipInventoryValidation={true}
                            onSelectProduct={selectProductHandler}
                            productIsConfigurableMessage={siteMessage("ListUpload_CannotOrderConfigurable")}
                            productIsUnavailableMessage={siteMessage("Product_NotFound")}
                            extendedStyles={styles.productSelectorStyles}
                        />
                        <ProductSelectorVariantModal skipInventoryValidation={true} />
                        <Link {...styles.uploadItemsLink} onClick={uploadItemsClickHandler}>
                            {translate("Upload Items")}
                        </Link>
                        <Icon
                            {...styles.closeAddItemsToList}
                            onClick={() => {
                                setAddItemsToListIsOpen(false);
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.uploadItemsGridItem}>
                        <Modal
                            {...styles.uploadItemsModal}
                            headline={translate("Upload Items To Quote")}
                            isOpen={uploadItemsModalIsOpen}
                            handleClose={uploadItemsModalCloseHandler}
                        >
                            <StyledWrapper>
                                <StyledWrapper>
                                    <OrderUpload
                                        descriptionText={siteMessage("Lists_UploadLinkTooltip")}
                                        checkInventory={false}
                                        templateUrl="/Excel/ListUploadTemplate.xlsx"
                                        onUploadProducts={uploadProductsHandler}
                                        extendedStyles={styles.orderUploadStyles}
                                    />
                                    <OrderUploadErrorsModal
                                        descriptionText={siteMessage("ListUpload_UploadError")}
                                        uploadErrorText={siteMessage("ListUpload_UploadError")}
                                        rowsLimitExceededText={siteMessage("ListUpload_RowsLimitExceeded")}
                                        errorReasons={{
                                            [UploadError.ConfigurableProduct]: siteMessage(
                                                "ListUpload_CannotOrderConfigurable",
                                            ),
                                            [UploadError.StyledProduct]: siteMessage("ListUpload_CannotOrderStyled"),
                                            [UploadError.InvalidUnit]: translate("Invalid U/M"),
                                            [UploadError.NotFound]: siteMessage("Product_NotFound"),
                                        }}
                                        extendedStyles={styles.orderUploadErrorsModalStyles}
                                    />
                                </StyledWrapper>
                                <StyledWrapper>
                                    <Zone zoneName="UploadInstructions" contentId={widgetId} />
                                </StyledWrapper>
                            </StyledWrapper>
                        </Modal>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <CreateNewGroupModal
                productInfo={productInfo}
                creatingNewGroup={creatingNewGroup}
                groupingModalIsOpen={groupingModalIsOpen}
                closeModalCallback={closeModalHandler}
                cart={cart}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqRequestQuoteOptions);
